// Ported from calm-hub-ui/src/visualizer/components/reactflow/utils/calmTransformer.ts.
// Keep logic in sync until the shared renderer package extraction.

import type { Node, Edge } from 'reactflow';
import {
    getLayoutedElements,
    createTopLevelLayout,
    calculateChildBounds,
    sortContainersDeepestFirst,
    sortNodesParentsBeforeChildren,
} from './layoutUtils';
import { identifyContainerNodes, MAX_COPIES, parseNodes } from './nodeParser';
import { expandEdgesToInstances, extractFlowTransitions, parseRelationships } from './relationshipParser';
import { GRAPH_LAYOUT } from './constants';
import type { LabCalmDoc, LabCalmNode } from './types';

/**
 * Result of parsing CALM data into ReactFlow elements
 */
export interface ParsedCALMData {
    nodes: Node[];
    edges: Edge[];
    /** True when a limit drew each node in its last container only. */
    copiesLimited?: boolean;
}

/**
 * Parses CALM architecture data into ReactFlow nodes and edges
 */
export function parseCALMData(data: LabCalmDoc | null | undefined, onShowDetailsCallback?: (node: LabCalmNode) => void): ParsedCALMData {
    if (!data) return { nodes: [], edges: [] };

    try {
        const relationships = data.relationships || [];
        const nodes = data.nodes || [];
        const flows = data.flows || [];

        const containerInfo = identifyContainerNodes(relationships, nodes);
        const flowTransitions = extractFlowTransitions(flows);
        const relationshipEdges = parseRelationships(relationships, flowTransitions);
        let parsed = parseNodes(nodes, containerInfo, onShowDetailsCallback);
        let edges = expandEdgesToInstances(relationshipEdges, parsed.instancesOf, parsed.parentOf);
        // Each copy of a node also gets the node's edges
        if (edges.length - relationshipEdges.length > MAX_COPIES) {
            parsed = parseNodes(nodes, containerInfo, onShowDetailsCallback, true);
            edges = expandEdgesToInstances(relationshipEdges, parsed.instancesOf, parsed.parentOf);
        }

        return { ...applyLayout(parsed.regularNodes, parsed.systemNodes, edges), copiesLimited: parsed.copiesLimited };
    } catch (error) {
        console.error('Error parsing CALM data:', error);
        return { nodes: [], edges: [] };
    }
}

/**
 * Applies layout to nodes, handling system nodes and their children
 */
function applyLayout(regularNodes: Node[], systemNodes: Node[], edges: Edge[]): ParsedCALMData {
    const { nodesWithParents, nodesWithoutParents, topLevelSystemNodes } = separateNodesByParent(
        regularNodes,
        systemNodes
    );

    layoutChildrenWithinSystems(systemNodes, nodesWithParents, edges);
    const positions = layoutTopLevelNodes(nodesWithoutParents, topLevelSystemNodes, nodesWithParents, edges);

    applyPositionsToNodes(nodesWithoutParents, positions);
    applyPositionsToNodes(topLevelSystemNodes, positions);

    const allNodes = combineNodes(topLevelSystemNodes, nodesWithoutParents, nodesWithParents, systemNodes);

    return { nodes: allNodes, edges };
}

/**
 * Separates nodes into groups based on whether they have a parent
 */
function separateNodesByParent(regularNodes: Node[], systemNodes: Node[]): { nodesWithParents: Node[]; nodesWithoutParents: Node[]; topLevelSystemNodes: Node[] } {
    const nodesWithParents: Node[] = [];
    const nodesWithoutParents: Node[] = [];
    const topLevelSystemNodes: Node[] = [];

    regularNodes.forEach((node) => {
        if (node.parentId) {
            nodesWithParents.push(node);
        } else {
            nodesWithoutParents.push(node);
        }
    });

    systemNodes.forEach((node) => {
        if (node.parentId) {
            nodesWithParents.push(node);
        } else {
            nodesWithoutParents.push(node);
            topLevelSystemNodes.push(node);
        }
    });

    return { nodesWithParents, nodesWithoutParents, topLevelSystemNodes };
}

/**
 * Layouts children within each system node and calculates system dimensions
 */
function layoutChildrenWithinSystems(systemNodes: Node[], nodesWithParents: Node[], edges: Edge[]): void {
    // Lay out the most deeply nested containers first so that an outer
    // container is sized once its inner container children already know their
    // own dimensions.
    sortContainersDeepestFirst(systemNodes).forEach((systemNode) => {
        const childNodes = nodesWithParents.filter((n) => n.parentId === systemNode.id);

        if (childNodes.length > 0) {
            layoutSystemWithChildren(systemNode, childNodes, nodesWithParents, edges);
        } else {
            setDefaultSystemDimensions(systemNode);
        }
    });
}

/**
 * Layouts a system node with its children
 */
function layoutSystemWithChildren(systemNode: Node, childNodes: Node[], nodesWithParents: Node[], edges: Edge[]): void {
    const systemEdges = edges.filter(
        (e) => childNodes.some((n) => n.id === e.source) && childNodes.some((n) => n.id === e.target)
    );

    const { nodes: layoutedChildren } = getLayoutedElements(childNodes, systemEdges);
    const bounds = calculateChildBounds(layoutedChildren);
    const padding = GRAPH_LAYOUT.SYSTEM_NODE_PADDING;

    const width = bounds.maxX - bounds.minX + padding * 2;
    const height = bounds.maxY - bounds.minY + padding * 2;

    systemNode.width = width;
    systemNode.height = height;
    systemNode.style = { ...systemNode.style, width, height };

    layoutedChildren.forEach((child) => {
        const originalChild = nodesWithParents.find((n) => n.id === child.id);
        if (originalChild) {
            originalChild.position = {
                x: child.position.x - bounds.minX + padding,
                y: child.position.y - bounds.minY + padding,
            };
        }
    });
}

/**
 * Sets default dimensions for a system node without children
 */
function setDefaultSystemDimensions(systemNode: Node): void {
    const width = GRAPH_LAYOUT.SYSTEM_NODE_DEFAULT_WIDTH;
    const height = GRAPH_LAYOUT.SYSTEM_NODE_DEFAULT_HEIGHT;

    systemNode.width = width;
    systemNode.height = height;
    systemNode.style = { ...systemNode.style, width, height };
}

/**
 * Creates the top-level layout for nodes not inside systems
 */
function layoutTopLevelNodes(nodesWithoutParents: Node[], topLevelSystemNodes: Node[], nodesWithParents: Node[], edges: Edge[]): Map<string, { x: number; y: number }> {
    const systemNodesForLayout = topLevelSystemNodes.map((s) => ({ ...s }));

    const topLevelEdges = edges.filter((e) => {
        const sourceInSystem = nodesWithParents.some((n) => n.id === e.source);
        const targetInSystem = nodesWithParents.some((n) => n.id === e.target);
        return !sourceInSystem || !targetInSystem;
    });

    const topLevelNodes = [...nodesWithoutParents, ...systemNodesForLayout];
    return createTopLevelLayout(topLevelNodes, topLevelEdges);
}

/**
 * Applies positions from layout to nodes
 */
function applyPositionsToNodes(nodes: Node[], positions: Map<string, { x: number; y: number }>): void {
    nodes.forEach((node) => {
        const pos = positions.get(node.id);
        if (pos) {
            node.position = pos;
        }
    });
}

/**
 * Combines all nodes in the correct order for ReactFlow
 */
function combineNodes(topLevelSystemNodes: Node[], nodesWithoutParents: Node[], nodesWithParents: Node[], systemNodes: Node[]): Node[] {
    return sortNodesParentsBeforeChildren([
        ...topLevelSystemNodes,
        ...nodesWithoutParents.filter((n) => !systemNodes.includes(n)),
        ...nodesWithParents,
    ]);
}
